/**
 * 下拉操作菜单组件。
 *
 * 该组件提供一个通用的"更多操作"下拉菜单，常用于列表行中的批量操作入口。
 * 点击触发按钮后展开下拉菜单项列表，支持以下特性：
 * - 自定义菜单项：通过 `items` 属性传入，每项包含标签、回调、图标、危险标记等
 * - 智能方向：根据触发按钮在视口中的位置自动选择向上或向下展开
 * - 单例互斥：同一页面同时只有一个菜单可展开，打开新菜单自动关闭旧菜单
 * - 外部点击关闭：点击菜单外部任意区域自动关闭
 * - ESC 键关闭：按 Escape 键关闭菜单
 * - 危险操作分隔线：danger 类型菜单项前自动插入分隔线
 * - 禁用项：支持禁用状态和禁用提示文案
 *
 * 导出：`CrmActionMenu`（自定义元素 `crm-action-menu`）、`MenuItem` 接口
 *
 * 依赖模块：`BaseElement`（基础元素）
 */
import { css, html } from 'lit';
import type { TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { BaseElement } from '../base-element.js';

/** 菜单项配置接口，描述单个下拉菜单项的属性和行为 */
export interface MenuItem {
  /** 菜单项显示文本 */
  label: string;
  /** 点击时执行的回调函数 */
  action: () => void;
  /** 可选的 SVG 图标模板（TemplateResult） */
  icon?: TemplateResult;
  /** 是否为危险操作（显示红色文字，前自动插入分隔线） */
  danger?: boolean;
  /** 是否禁用（禁用时不可点击，显示灰色） */
  disabled?: boolean;
  /** 禁用时的提示文案（显示在菜单项右侧） */
  disabledHint?: string;
}

/** 全局唯一 ID 计数器，用于区分不同菜单实例 */
let _uid = 0;

/**
 * 下拉操作菜单组件。
 *
 * 继承 `BaseElement`，通过 `@customElement('crm-action-menu')` 注册为自定义元素。
 * 通过 `items` 属性配置菜单项列表，点击触发按钮展开/收起下拉菜单。
 * 支持单例互斥：打开新菜单时通过自定义事件通知其他菜单关闭。
 */
@customElement('crm-action-menu')
export class CrmActionMenu extends BaseElement {
  /** 菜单项配置数组 */
  @property({ type: Array }) items: MenuItem[] = [];

  /** 菜单是否展开 */
  @state() private open = false;
  /** 是否向上展开（根据触发按钮在视口位置自动判断） */
  @state() private dropUp = false;

  /** 当前组件实例的唯一 ID，用于单例互斥时区分不同菜单 */
  private _uid = ++_uid;
  /** 外部点击事件处理器引用（用于卸载时移除监听） */
  private _clickOutsideHandler: ((e: Event) => void) | null = null;
  /** ESC 键事件处理器引用（用于卸载时移除监听） */
  private _keydownHandler: ((e: KeyboardEvent) => void) | null = null;
  /** 其他菜单打开事件处理器引用（用于单例互斥） */
  private _otherMenuOpenHandler: ((e: Event) => void) | null = null;

  static styles = css`
    /* 宿主元素：内联块级，相对定位作为下拉菜单的定位上下文 */
    :host {
      display: inline-block;
      position: relative;
    }
    /* 触发按钮：小号文字，悬停变深色 */
    .trigger {
      color: #60646c;
      font-size: 12px;
      font-weight: 500;
      padding: 6px 10px;
      border-radius: 4px;
      cursor: pointer;
      transition: color 0.15s;
      background: none;
      border: none;
      user-select: none;
    }
    .trigger:hover {
      color: #1c2024;
    }
    /* 下拉菜单容器：绝对定位，右对齐，白底圆角带阴影 */
    .dropdown {
      position: absolute;
      right: 0;
      background: #ffffff;
      border: 1px solid #e0e1e6;
      border-radius: 8px;
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
      min-width: 150px;
      z-index: 1000;
      padding: 4px;
    }
    /* 向下展开：定位在触发按钮下方 */
    .dropdown.down {
      top: 100%;
      margin-top: 4px;
    }
    /* 向上展开：定位在触发按钮上方 */
    .dropdown.up {
      bottom: 100%;
      margin-bottom: 4px;
    }
    /* 菜单项图标容器：固定尺寸，居中对齐 */
    .item-icon {
      display: flex;
      align-items: center;
      width: 16px;
      height: 16px;
      flex-shrink: 0;
    }
    .item-icon svg {
      width: 14px;
      height: 14px;
    }
    /* 菜单项：flex 布局，悬停浅灰背景，禁止换行 */
    .item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 6px 10px;
      font-size: 13px;
      color: #1c2024;
      border-radius: 4px;
      cursor: pointer;
      transition: background 0.1s;
      white-space: nowrap;
    }
    .item:hover {
      background: #f3f4f6;
    }
    /* 危险菜单项：红色文字，悬停浅红背景 */
    .item.danger {
      color: #dc2626;
    }
    .item.danger:hover {
      background: #fef2f2;
    }
    /* 禁用菜单项：灰色文字，不可点击 */
    .item.disabled {
      color: #9ca3af;
      cursor: not-allowed;
    }
    .item.disabled:hover {
      background: transparent;
    }
    /* 禁用提示文案：右对齐小号灰色文字 */
    .disabled-hint {
      font-size: 11px;
      color: #9ca3af;
      margin-left: auto;
      padding-left: 8px;
    }
    /* 分隔线：水平细线，用于隔离危险操作 */
    .divider {
      height: 1px;
      background: #e0e1e6;
      margin: 4px 0;
    }
  `;

  /**
   * 生命周期：组件挂载时注册三个全局事件监听。
   *
   * - `_clickOutsideHandler`：监听 document click，点击菜单外部时关闭菜单
   * - `_keydownHandler`：监听 ESC 键关闭菜单
   * - `_otherMenuOpenHandler`：监听 `crm-action-menu-open` 自定义事件，
   *   当其他菜单打开时（uid 不同）自动关闭当前菜单，实现单例互斥
   */
  connectedCallback() {
    super.connectedCallback();
    this._clickOutsideHandler = (e: Event) => {
      if (!this.contains(e.target as Node)) {
        this.open = false;
      }
    };
    this._keydownHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') this.open = false;
    };
    this._otherMenuOpenHandler = (e: Event) => {
      const ce = e as CustomEvent;
      // 仅当事件来自其他菜单实例（uid 不同）时关闭当前菜单
      if (ce.detail?.uid !== this._uid) this.open = false;
    };
    document.addEventListener('click', this._clickOutsideHandler);
    document.addEventListener('keydown', this._keydownHandler);
    document.addEventListener('crm-action-menu-open', this._otherMenuOpenHandler);
  }

  /** 生命周期：组件卸载时移除所有全局事件监听，防止内存泄漏 */
  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._clickOutsideHandler) {
      document.removeEventListener('click', this._clickOutsideHandler);
    }
    if (this._keydownHandler) {
      document.removeEventListener('keydown', this._keydownHandler);
    }
    if (this._otherMenuOpenHandler) {
      document.removeEventListener('crm-action-menu-open', this._otherMenuOpenHandler);
    }
  }

  /**
   * 切换菜单展开/收起状态。
   *
   * 打开菜单时：
   * - 根据触发按钮在视口中的位置决定展开方向（顶部超过 70% 视口高度时向上展开）
   * - 派发 `crm-action-menu-open` 自定义事件（携带当前 uid），通知其他菜单关闭
   */
  private toggle() {
    this.open = !this.open;
    if (this.open) {
      // 获取触发按钮位置，若位于视口下方 70% 以下则向上展开，避免被视口底部裁切
      const rect = this.getBoundingClientRect();
      this.dropUp = rect.top > window.innerHeight * 0.7;
      // 派发单例互斥事件，其他菜单收到后会自动关闭
      this.dispatchEvent(new CustomEvent('crm-action-menu-open', {
        detail: { uid: this._uid },
        bubbles: true,
        composed: true,
      }));
    }
  }

  /**
   * 处理菜单项点击事件。
   *
   * 阻止事件冒泡（防止触发外部 click 监听），禁用项直接返回，
   * 否则关闭菜单并执行该项的 action 回调。
   *
   * @param item - 被点击的菜单项配置
   * @param e - 点击事件对象
   */
  private handleItemClick(item: MenuItem, e: Event) {
    e.stopPropagation();
    if (item.disabled) return;
    this.open = false;
    item.action();
  }

  /**
   * 判断是否在某个菜单项前插入分隔线。
   *
   * 仅在 danger 菜单项前且前一项不是 danger 时插入分隔线，
   * 将危险操作（如删除）与普通操作视觉分离。
   * 第一项（index === 0）不插入分隔线。
   *
   * @param item - 当前菜单项
   * @param index - 当前菜单项在列表中的索引
   * @returns 是否需要插入分隔线
   */
  private hasDividerBefore(item: MenuItem, index: number): boolean {
    if (index === 0) return false;
    return !!item.danger && !this.items[index - 1].danger;
  }

  /**
   * 主渲染方法。
   *
   * 渲染触发按钮（默认文案"更多"，可通过 slot 覆盖）+ 下拉箭头图标。
   * 菜单展开时渲染下拉列表，每项前按需插入分隔线。
   * 菜单项支持图标、危险标记、禁用状态和禁用提示。
   *
   * @returns Lit 模板结果
   */
  render() {
    return html`
      <!-- 触发按钮：点击切换菜单展开/收起，stopPropagation 防止冒泡到 document click 监听 -->
      <button class="trigger" @click=${(e: Event) => { e.stopPropagation(); this.toggle(); }}>
        <slot>更多</slot>
        <span style="font-size:10px;margin-left:2px;">▾</span>
      </button>
      <!-- 下拉菜单：仅 open 为 true 时渲染，根据 dropUp 决定向上或向下展开 -->
      ${this.open ? html`
        <div class="dropdown ${this.dropUp ? 'up' : 'down'}" @click=${(e: Event) => e.stopPropagation()}>
          ${this.items.map((item, i) => html`
            ${this.hasDividerBefore(item, i) ? html`<div class="divider"></div>` : ''}
            <div
              class="item ${item.danger ? 'danger' : ''} ${item.disabled ? 'disabled' : ''}"
              @click=${(e: Event) => this.handleItemClick(item, e)}
            >
              ${item.icon ? html`<span class="item-icon">${item.icon}</span>` : ''}
              ${item.label}
              ${item.disabled && item.disabledHint ? html`
                <span class="disabled-hint">${item.disabledHint}</span>
              ` : ''}
            </div>
          `)}
        </div>
      ` : ''}
    `;
  }
}
